import * as React from 'react';
import { IFinancial } from '../../../../data/entities/customer/financial';

import { ICustomer } from '../../../../data/entities/customer/index';
import { Accordion, AccordionHeader, AccordionItem } from '../../../components/accordion';
import { IAddress, ManualAddressInput } from '../../../components/address';
import { Form, FormInput, FormLabel, FormRow } from '../../../components/form';
import { FormRadio } from '../../../components/form/form-radio/FormRadio';
import { Heading } from '../../../components/heading/Heading';
import { Loader } from '../../../components/loader/Loader';
import { Panel } from '../../../components/panel/Panel';
import { Paragraph } from '../../../components/paragraph/Paragraph';
import { PaymentDetailsEdit } from '../payment/CustomerPaymentDetails';
import { IValidationResult } from '../validation';
import { addressKeyMap, AddressTypes, groupAddresses } from './utils';

type FinancialKeys = keyof IFinancial;

interface IBusinessDetailsProps {
	customer: ICustomer;
	financial: IFinancial;
	validation: IValidationResult[];
	onChange: (key: keyof ICustomer, value: ICustomer[keyof ICustomer]) => any;
	onChangeFinancial: (key: FinancialKeys | FinancialKeys[], value: IFinancial[FinancialKeys] | Array<IFinancial[FinancialKeys]>) => any;
	isImpersonating: boolean;
}

interface IBusinessDetailsState {
	vatChecked: boolean;
	companyNamesAreSame: boolean;
}

export class BusinessDetailsEdit extends React.Component<IBusinessDetailsProps, IBusinessDetailsState> {
	constructor(props: IBusinessDetailsProps) {
		super(props);
		this.state = {
			vatChecked: !!this.props.financial.vat_number,
			companyNamesAreSame: this.props.financial.trading_name ?
				(this.props.financial.trading_name === this.props.customer.company_name) : true
		};
		this.onChangeAddress.bind(this);
	}

	public onChangeAddress(addressType: AddressTypes[], next: IAddress) {

		const { keys, values } = addressType.reduce((prev, curr: any) => {
			const addressTypeKeys = Object.keys(next).map((k) => addressKeyMap(curr)[k]);
			return { keys: [...prev.keys, ...addressTypeKeys], values: [...prev.values, ...Object.values(next)] };
		}, { keys: [], values: [] });

		this.props.onChangeFinancial(keys, values);

	}

	public render() {

		const { customer, financial, validation, isImpersonating } = this.props;

		const { vatChecked, companyNamesAreSame } = this.state;

		const hasVAT = !!financial.vat_number || vatChecked;

		const { correspondence } = groupAddresses(financial);

		return (!customer || !financial) ? <Loader loading={true} /> : (
			<>
				<div>
					<Accordion>
						<AccordionHeader>Business details</AccordionHeader>
						<AccordionItem>
							{!!validation && <>
								<Panel withIcon={true}>
									We need both your business details and payment details in order to pay you for bookings. Please fill in both sections and save your changes.
								</Panel>
							</>}
							<Form>
								<Heading type="B1">Name</Heading>
								<FormRow>
									<FormLabel htmlFor="company_name">Company name</FormLabel>
									<FormInput
										name={'company_name'}
										onChange={(e) => {
											this.props.onChange('company_name', e.target.value);
											if (companyNamesAreSame) {
												this.props.onChangeFinancial('trading_name', e.target.value);
											}
										}}
										type="text"
										required
										id={`business_company_name`}
										value={customer.company_name}
									/>
								</FormRow>

								<FormRow >
									<div className={'customer-business-details__checkbox_row'}>
										<FormInput
											id={'companyNamesAreSame'}
											name={'company_names_are_same'}
											type="checkbox"
											defaultChecked={companyNamesAreSame}
											value={companyNamesAreSame.toString()}
											onChange={(e) => {
												this.setState(
													{ companyNamesAreSame: e.target.checked },
													() => this.props.onChangeFinancial('trading_name', customer.company_name)
												);
											}}
										/>
										<FormLabel htmlFor="company_names_are_same">Company name and trading name are the same</FormLabel>
									</div>
								</FormRow>

								{!companyNamesAreSame &&
									<FormRow>
										<FormLabel htmlFor="trading_name">Trading name</FormLabel>
										<FormInput
											name={'trading_name'}
											onChange={(e) => this.props.onChangeFinancial('trading_name', e.target.value)}
											type="text"
											required
											id={`business_trading_name`}
											value={financial.trading_name}
										/>
									</FormRow>
								}

								<Heading type="B1">Address</Heading>
								<Paragraph fillParent={'full'} additionalClasses={['customer-business-details__p_section_help']}>
									This should be your business' correspondence address.
								</Paragraph>

								<FormRow>
									<ManualAddressInput
										address={correspondence}
										onChange={(next) => this.onChangeAddress([AddressTypes.REGISTERED, AddressTypes.CORRESPONDENCE], next)}
										validation={validation}
										// Certain bank accounts are excluded due to sanctions
										excludedCountries={['RU', 'BY']}
									/>
								</FormRow>
								<Heading type="B1">VAT</Heading>

								<FormRow>
									<div className={'customer-business-details customer-business-details__div_flex-row'}>
										<FormRadio
											name="has_vat"
											value={'yes'}
											onChange={(e) => this.setState({ vatChecked: true })}
											checked={hasVAT}
											label={'I am VAT registered'}
										/>
										<FormRadio
											name="has_vat"
											value={'no'}
											onChange={(e) => {
												this.props.onChangeFinancial('vat_number', '');
												this.setState({ vatChecked: false });
											}}
											checked={!hasVAT}
											label={'I am not VAT registered'}
										/>
									</div>
								</FormRow>
								{hasVAT && <FormRow>
									<FormLabel htmlFor="vat_number">VAT number</FormLabel>
									<FormInput
										name={'vat_number'}
										onChange={(e) => this.props.onChangeFinancial('vat_number', e.target.value)}
										type="text"
										required
										id={`business_vat_number`}
										value={financial.vat_number}
									/>
								</FormRow>}
							</Form>
						</AccordionItem>
					</Accordion>
				</div>
				{
					!isImpersonating &&
					<PaymentDetailsEdit
						validation={validation}
						financial={financial}
						onChangeFinancial={this.props.onChangeFinancial}
					/>
				}
			</>
		);
	}
}
